"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

interface NewsImageSliderProps {
  images: string[];
  title: string;
  className?: string;
}

export default function NewsImageSlider({
  images,
  title,
  className = "",
}: NewsImageSliderProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const mouseStartX = useRef(0);
  const mouseEndX = useRef(0);
  const isDragging = useRef(false);

  if (!images || images.length === 0) {
    return null;
  }

  const goToImage = (index: number) => {
    if (index === currentImageIndex) return;
    setCurrentImageIndex(index);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Cleanup effect for mouse event listeners
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && images.length > 1) {
      nextImage();
    }
    if (isRightSwipe && images.length > 1) {
      prevImage();
    }
  };

  // Mouse handlers for drag navigation on desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    setIsDragActive(true);
    mouseStartX.current = e.clientX;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.current) return;
    mouseEndX.current = e.clientX;
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setIsDragActive(false);

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    if (!mouseStartX.current || !mouseEndX.current) return;

    const distance = mouseStartX.current - mouseEndX.current;
    const isLeftDrag = distance > 50;
    const isRightDrag = distance < -50;

    if (isLeftDrag && images.length > 1) {
      nextImage();
    }
    if (isRightDrag && images.length > 1) {
      prevImage();
    }

    // Reset values
    mouseStartX.current = 0;
    mouseEndX.current = 0;
  };

  return (
    <div
      className={`relative w-full rounded-2xl overflow-hidden select-none transition-all duration-200 ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      style={{ userSelect: "none" }}
    >
      {/* Image Container */}
      <div className="relative w-full h-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            {images[currentImageIndex] && (
              <Image
                src={images[currentImageIndex] || ""}
                alt={title}
                fill
                className="object-cover select-none"
                draggable={false}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Custom Dots - Only show if multiple images */}
      {images.length > 1 && (
        <div className="absolute bottom-[38px] left-1/2 transform -translate-x-1/2 flex gap-3">
          {images.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToImage(index)}
              className={`transition-all duration-500 ease-out bg-white touch-manipulation ${
                index === currentImageIndex
                  ? "w-6 h-3" // Active: 24px x 12px
                  : "w-3 h-3 opacity-50" // Inactive: 12px x 12px with 0.5 opacity
              }`}
              style={{
                borderRadius: "15px", // Same as hero slider
              }}
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.1 },
              }}
              animate={{
                width: index === currentImageIndex ? "24px" : "12px",
                height: index === currentImageIndex ? "12px" : "12px",
                opacity: index === currentImageIndex ? 1 : 0.5,
              }}
              transition={{
                width: { duration: 0.4, ease: [0.23, 1, 0.32, 1] },
                height: { duration: 0.4, ease: [0.23, 1, 0.32, 1] },
                opacity: { duration: 0.3 },
              }}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
